import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Wait for all fonts to be loaded
async function waitForFonts(): Promise<void> {
  await document.fonts.ready;
  // Load Lato explicitly if not already loaded
  try {
    await document.fonts.load('400 16px Lato');
    await document.fonts.load('600 16px Lato');
    await document.fonts.load('700 16px Lato');
  } catch {
    // Font may already be loaded or not available
  }
  // Additional delay to ensure fonts are fully applied
  await new Promise((resolve) => setTimeout(resolve, 300));
}

// Wait for all images in element to load
async function waitForImages(element: HTMLElement): Promise<void> {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve; // Continue even if image fails
    });
  });
  await Promise.all(promises);
}

// Create a hidden clone for capture with export styles applied
function createExportClone(element: HTMLElement): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;

  // Get original dimensions
  const originalWidth = element.offsetWidth;
  const originalHeight = element.offsetHeight;

  // Position off-screen but still rendered with explicit dimensions
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  clone.style.top = '0';
  clone.style.transform = 'none';
  clone.style.width = `${originalWidth}px`;
  clone.style.minHeight = `${originalHeight}px`;

  // Apply letter-spacing to fix html2canvas font rendering issues
  const letterSpacing = '0.02em';
  clone.style.letterSpacing = letterSpacing;
  const allElements = clone.querySelectorAll('*');
  allElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.letterSpacing = letterSpacing;
    }
  });

  document.body.appendChild(clone);
  return clone;
}

export async function generatePDF(
  element: HTMLElement,
  filename: string = 'report.pdf'
): Promise<void> {
  // Wait for fonts and images to load
  await waitForFonts();
  await waitForImages(element);

  // Create hidden clone with export styles
  const clone = createExportClone(element);

  // Wait for clone to render
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Get clone dimensions
  const cloneWidth = clone.offsetWidth;
  const cloneHeight = clone.offsetHeight;

  // Create canvas from cloned element
  const canvas = await html2canvas(clone, {
    scale: 2, // Higher quality
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: '#ffffff',
    width: cloneWidth,
    height: cloneHeight,
    windowWidth: cloneWidth,
    windowHeight: cloneHeight,
  });

  // Remove clone
  document.body.removeChild(clone);

  // A4 dimensions in mm
  const pageWidth = 210; // A4 portrait width
  const pageHeight = 297; // A4 portrait height
  const margin = 10; // 10mm margins

  // Calculate content dimensions (with margins)
  const contentWidth = pageWidth - (margin * 2);
  const contentHeight = pageHeight - (margin * 2);

  // Calculate how to fit the canvas width to page width
  const scale = contentWidth / (canvas.width / 2); // Divide by 2 because canvas is 2x scale
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height / 2) * scale; // Divide by 2 because canvas is 2x scale

  // Create PDF in portrait orientation
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Calculate how many pages we need
  const totalPages = Math.ceil(imgHeight / contentHeight);

  // Add content across multiple pages
  for (let page = 0; page < totalPages; page++) {
    if (page > 0) {
      pdf.addPage();
    }

    // Calculate the portion of the image to show on this page
    const sourceY = (page * contentHeight) / scale * 2; // Multiply by 2 for canvas scale
    const sourceHeight = Math.min(contentHeight / scale * 2, (canvas.height - sourceY));

    // Create a temporary canvas for this page's content
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourceHeight;

    const ctx = pageCanvas.getContext('2d');
    if (ctx) {
      // Draw the relevant portion of the original canvas
      ctx.drawImage(
        canvas,
        0, sourceY,
        canvas.width, sourceHeight,
        0, 0,
        canvas.width, sourceHeight
      );

      // Add this page's content to PDF
      const pageImgData = pageCanvas.toDataURL('image/png');
      const pageImgHeight = (sourceHeight / 2) * scale;
      pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, pageImgHeight);
    }
  }

  // Download the PDF
  pdf.save(filename);
}

export async function generateImage(
  element: HTMLElement,
  filename: string = 'report.png',
  format: 'png' | 'jpeg' = 'png'
): Promise<void> {
  // Wait for fonts and images to load
  await waitForFonts();
  await waitForImages(element);

  // Create hidden clone with export styles
  const clone = createExportClone(element);

  // Wait for clone to render
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Get clone dimensions
  const cloneWidth = clone.offsetWidth;
  const cloneHeight = clone.offsetHeight;

  // Create canvas from cloned element
  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: format === 'jpeg' ? '#ffffff' : null,
    width: cloneWidth,
    height: cloneHeight,
    windowWidth: cloneWidth,
    windowHeight: cloneHeight,
  });

  // Remove clone
  document.body.removeChild(clone);

  // Convert to image and download
  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const imgData = canvas.toDataURL(mimeType, 0.95);

  const link = document.createElement('a');
  link.download = filename;
  link.href = imgData;
  link.click();
}

export async function generatePDFBlob(
  element: HTMLElement
): Promise<Blob> {
  // Wait for fonts and images to load
  await waitForFonts();
  await waitForImages(element);

  // Create hidden clone with export styles
  const clone = createExportClone(element);

  // Wait for clone to render
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Get clone dimensions
  const cloneWidth = clone.offsetWidth;
  const cloneHeight = clone.offsetHeight;

  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: null,
    width: cloneWidth,
    height: cloneHeight,
    windowWidth: cloneWidth,
    windowHeight: cloneHeight,
  });

  // Remove clone
  document.body.removeChild(clone);

  // A4 landscape width in mm
  const pageWidth = 297;

  // Fit to page width, maintaining aspect ratio
  const canvasRatio = canvas.width / canvas.height;
  const imgWidth = pageWidth;
  const imgHeight = pageWidth / canvasRatio;

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  // Add image to PDF at top-left (no padding)
  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

  return pdf.output('blob');
}
